import axios from 'axios';
import { normalizeExportResponse } from '../utils/exportResponse';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://web-api-rqa6jhyvwa-uc.a.run.app';

// Debug logging for environment variables
console.log('🔍 Debug API Configuration:');
console.log('VITE_API_URL env var:', import.meta.env.VITE_API_URL);
console.log('Final API_BASE_URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const authApi = {
  getContext: async () => {
    const response = await api.get('/api/v1/auth/context');
    return response.data;
  },

  selectOrganization: async (orgId) => {
    const response = await api.post('/api/v1/auth/organization/select', { orgId });
    return response.data;
  },
};

export const videoApi = {
  // Get presigned upload URL
  getPresignedUrl: async (filename) => {
    const response = await api.post('/api/v1/videos/presign', { filename });
    return response.data;
  },

  // Mark upload as complete
  completeUpload: async (jobId) => {
    const response = await api.post('/api/v1/videos/complete', { job_id: jobId });
    return response.data;
  },

  // Get job status
  getJobStatus: async (jobId) => {
    const response = await api.get(`/api/v1/videos/status/${jobId}`);
    return response.data;
  },

  // Get job results
  getJobResults: async (jobId) => {
    const response = await api.get(`/api/v1/videos/results/${jobId}`);
    return response.data;
  },

  // Get all jobs
  getAllJobs: async (limit = 50, options = {}) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', String(limit));
    if (options.offset) params.append('offset', String(options.offset));
    if (options.statuses && options.statuses.length) params.append('statuses', options.statuses.join(','));
    if (options.createdFrom) params.append('createdFrom', options.createdFrom);
    if (options.createdTo) params.append('createdTo', options.createdTo);
    if (options.q) params.append('q', options.q);
    const response = await api.get(`/api/v1/videos?${params.toString()}`);
    return response.data;
  },

  // Download results
  downloadResults: async (jobId) => {
    const response = await api.get(`/api/v1/videos/download/${jobId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Export results as CSV (always includes all blendshapes). Optionally pass tz.
  exportResults: async (jobId, options = {}) => {
    const { tz } = options;
    const response = await api.get(`/api/v1/videos/export/${jobId}`, {
      params: { tz },
      responseType: 'blob',
    });
    return normalizeExportResponse(response);
  },

  // Batch export ZIP for multiple job IDs
  batchExport: async (jobIds, options = {}) => {
    const { format = 'csv', tz } = options;
    const params = new URLSearchParams();
    jobIds.forEach(id => params.append('jobIds', id));
    if (format) params.append('format', format);
    if (tz) params.append('tz', tz);
    const response = await api.get(`/api/v1/videos/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Batch delete jobs
  batchDelete: async (jobIds) => {
    const response = await api.post('/api/v1/videos/batch-delete', { jobIds });
    return response.data;
  },

  // Batch retry jobs
  batchRetry: async (jobIds) => {
    const response = await api.post('/api/v1/videos/batch-retry', { jobIds });
    return response.data;
  },

  // ============================================================================
  // ASYNC EXPORT METHODS - Story 5.1
  // ============================================================================

  // Create async export job
  createExportJob: async (jobIds, options = {}) => {
    const { format = 'csv', tz } = options;
    const response = await api.post('/api/v1/videos/export/jobs', {
      jobIds,
      format,
      tz
    });
    return response.data;
  },

  // Get export job status
  getExportJobStatus: async (exportJobId) => {
    const response = await api.get(`/api/v1/videos/export/jobs/${exportJobId}`);
    return response.data;
  },

  // List export jobs
  listExportJobs: async (options = {}) => {
    const { limit = 20, offset = 0, status } = options;
    const params = new URLSearchParams();
    if (limit) params.append('limit', String(limit));
    if (offset) params.append('offset', String(offset));
    if (status) params.append('status', status);
    const response = await api.get(`/api/v1/videos/export/jobs?${params.toString()}`);
    return response.data;
  },

  // Upload file directly to storage using presigned URL (supports cancellation via AbortSignal)
  uploadFile: async (presignedUrl, file, onProgress, signal) => {
    const fileName = (file.name || '').toLowerCase();
    let contentType = file.type;
    if (!contentType) {
      if (fileName.endsWith('.mov')) {
        contentType = 'video/quicktime';
      } else if (fileName.endsWith('.mp4')) {
        contentType = 'video/mp4';
      } else {
        contentType = 'application/octet-stream';
      }
    }

    return axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': contentType,
      },
      signal,
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(progress);
        }
      },
    });
  },
};

export default api; 